/**
 * Manual Entry API Client
 *
 * Note: Submission creation only requires period.
 * Employer info is pulled from BusinessProfile automatically.
 */

import {
  ManualSubmission,
  SubmissionListItem,
  SubmissionSummary,
  EmployeeCalculation,
  C10GenerationResult,
  CreateSubmissionRequest,
  AddEmployeeRequest,
  UpdateEmployeeRequest,
  UpdatePeriodRequest,
  BulkAddEmployeesRequest,
  BulkAddResult,
  AccessCheckResponse,
  LimitsResponse,
} from '@/types/nib/manualEntry';
import { ApiResponse } from '@/types';
import apiClient, { handleApiResponse } from './client';

// Field mapping: Frontend uses basic_wages, backend uses regular_wages
type BackendEmployeeRequest = Omit<AddEmployeeRequest, 'basic_wages'> & {
  regular_wages: number;
};

function toBackendRequest(
  data: AddEmployeeRequest | UpdateEmployeeRequest
): BackendEmployeeRequest {
  const { basic_wages, ...rest } = data;
  return { ...rest, regular_wages: basic_wages };
}

interface BackendEmployee {
  id: number;
  ni_number: string;
  first_name: string;
  last_name: string;
  regular_wages: number;
  gratuity?: number;
  weeks_worked: number;
  pay_indicator: string;
  category: number;
  was_capped: boolean;
  was_truncated: boolean;
  employee_contribution: number;
  employer_contribution: number;
  gratuity_contribution?: number;
  total_contribution: number;
}

// Backend summary uses different field names
interface BackendSummary {
  employee_count: number;
  total_regular_wages: number;
  total_gratuity: number;
  total_employee_contribution: number;
  total_employer_contribution: number;
  total_gratuity_contribution: number;
  total_contributions: number;
}

// Backend submission uses employer_ni_number instead of employer_number
interface BackendSubmission {
  id: number;
  employer_ni_number: string;
  company_name: string;
  contribution_period: string;
  format_type: string;
  is_draft: boolean;
  employees: BackendEmployee[];
  summary: BackendSummary;
  // Backend doesn't return is_valid/validation_errors
}

function fromBackendEmployee(emp: BackendEmployee): EmployeeCalculation {
  return {
    id: emp.id,
    ni_number: emp.ni_number,
    name: `${emp.first_name} ${emp.last_name}`,
    first_name: emp.first_name,
    last_name: emp.last_name,
    basic_wages: emp.regular_wages,
    gratuity_wages: emp.gratuity ?? 0,
    weeks_worked: emp.weeks_worked,
    pay_indicator: emp.pay_indicator as EmployeeCalculation['pay_indicator'],
    category: emp.category as EmployeeCalculation['category'],
    truncated_wages: 0,
    capped_wages: 0,
    was_capped: emp.was_capped,
    cap_applied: null,
    employee_contribution: emp.employee_contribution,
    employer_contribution: emp.employer_contribution,
    gratuity_contribution: emp.gratuity_contribution ?? 0,
    total_contribution: emp.total_contribution,
    is_valid: true,
    warnings: [],
  };
}

function fromBackendResponse(data: unknown): EmployeeCalculation {
  return fromBackendEmployee(data as BackendEmployee);
}

function fromBackendSummary(summary: BackendSummary): SubmissionSummary {
  return {
    total_employees: summary.employee_count,
    total_basic_wages: summary.total_regular_wages,
    total_gratuity_wages: summary.total_gratuity,
    total_employee_contributions: summary.total_employee_contribution,
    total_employer_contributions: summary.total_employer_contribution,
    total_gratuity_contributions: summary.total_gratuity_contribution,
    grand_total: summary.total_contributions,
  };
}

function fromBackendSubmission(data: BackendSubmission): ManualSubmission {
  return {
    id: data.id,
    employer_number: data.employer_ni_number,
    company_name: data.company_name,
    contribution_period: data.contribution_period,
    format_type: data.format_type as ManualSubmission['format_type'],
    is_draft: data.is_draft,
    employees: data.employees.map(fromBackendEmployee),
    summary: fromBackendSummary(data.summary),
    is_valid: true, // Backend doesn't return this
    validation_errors: [], // Backend doesn't return this
    created_at: null,
    last_edited_at: null,
  };
}

// Backend C10 response uses different field names
interface BackendC10Response {
  file_path?: string;
  file_name?: string;
  file_content?: string;
  record_count?: number;
  total_contribution?: number;
  success?: boolean;
  message?: string;
}

function fromBackendC10Response(data: BackendC10Response): C10GenerationResult {
  // Extract filename from file_path if file_name not provided
  let fileName = data.file_name;
  if (!fileName && data.file_path) {
    // Extract just the filename from the full path
    const pathParts = data.file_path.split('/');
    fileName = pathParts[pathParts.length - 1];
  }

  return {
    file_name: fileName || 'C10_download.txt',
    file_content: data.file_content || '',
    record_count: data.record_count ?? 0,
    total_contribution: data.total_contribution ?? 0,
  };
}

class ManualEntryApiClient {
  private basePath = '/v1/nib/manual';

  /**
   * Check user access to manual entry and get tier limits.
   * Returns business profile info and tier-based limits.
   */
  async checkAccess(): Promise<AccessCheckResponse> {
    const response = await apiClient.get<ApiResponse<AccessCheckResponse>>(
      `${this.basePath}/access`
    );
    return handleApiResponse(response);
  }

  /**
   * Get detailed tier limits and current usage.
   * Shows remaining capacity for profiles, employees, etc.
   */
  async getLimits(): Promise<LimitsResponse> {
    const response = await apiClient.get<ApiResponse<LimitsResponse>>(
      `${this.basePath}/limits`
    );
    return handleApiResponse(response);
  }

  /**
   * Create submission - only needs period!
   * Employer info pulled from BusinessProfile.
   */
  async createSubmission(
    data: CreateSubmissionRequest
  ): Promise<ManualSubmission> {
    const response = await apiClient.post<ApiResponse<ManualSubmission>>(
      `${this.basePath}/submissions`,
      data
    );
    return handleApiResponse(response);
  }

  async listSubmissions(
    includeCompleted = true
  ): Promise<SubmissionListItem[]> {
    const response = await apiClient.get<ApiResponse<SubmissionListItem[]>>(
      `${this.basePath}/submissions`,
      { params: { include_completed: includeCompleted } }
    );
    return handleApiResponse(response);
  }

  async getSubmission(submissionId: string): Promise<ManualSubmission> {
    const response = await apiClient.get<ApiResponse<BackendSubmission>>(
      `${this.basePath}/submissions/${submissionId}`
    );
    const data = handleApiResponse(response);
    // Transform entire response from backend format to frontend format
    return fromBackendSubmission(data);
  }

  async deleteSubmission(submissionId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `${this.basePath}/submissions/${submissionId}`
    );
    handleApiResponse(response);
  }

  /**
   * Update submission contribution period.
   * Can only update drafts.
   */
  async updatePeriod(
    submissionId: string,
    data: UpdatePeriodRequest
  ): Promise<ManualSubmission> {
    const response = await apiClient.put<ApiResponse<ManualSubmission>>(
      `${this.basePath}/submissions/${submissionId}`,
      data
    );
    return handleApiResponse(response);
  }

  async addEmployee(
    submissionId: string,
    data: AddEmployeeRequest
  ): Promise<EmployeeCalculation> {
    const response = await apiClient.post<ApiResponse<EmployeeCalculation>>(
      `${this.basePath}/submissions/${submissionId}/employees`,
      toBackendRequest(data)
    );
    return fromBackendResponse(handleApiResponse(response));
  }

  /**
   * Bulk add employees to submission.
   * Maximum 100 employees per request.
   */
  async bulkAddEmployees(
    submissionId: string,
    data: BulkAddEmployeesRequest
  ): Promise<BulkAddResult> {
    // Map each employee's basic_wages to regular_wages
    const backendData = {
      employees: data.employees.map(emp => toBackendRequest(emp)),
    };
    const response = await apiClient.post<ApiResponse<BulkAddResult>>(
      `${this.basePath}/submissions/${submissionId}/employees/bulk`,
      backendData
    );
    return handleApiResponse(response);
  }

  async updateEmployee(
    submissionId: string,
    employeeId: string,
    data: UpdateEmployeeRequest
  ): Promise<EmployeeCalculation> {
    const response = await apiClient.put<ApiResponse<EmployeeCalculation>>(
      `${this.basePath}/submissions/${submissionId}/employees/${employeeId}`,
      toBackendRequest(data)
    );
    return fromBackendResponse(handleApiResponse(response));
  }

  async removeEmployee(
    submissionId: string,
    employeeId: string
  ): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `${this.basePath}/submissions/${submissionId}/employees/${employeeId}`
    );
    handleApiResponse(response);
  }

  async generateC10(submissionId: string): Promise<C10GenerationResult> {
    const response = await apiClient.post<ApiResponse<BackendC10Response>>(
      `${this.basePath}/submissions/${submissionId}/generate`
    );
    const data = handleApiResponse(response);
    return fromBackendC10Response(data);
  }

  /**
   * Download Excel template for bulk employee import.
   * Returns a Blob for file download.
   */
  async downloadTemplate(): Promise<Blob> {
    const response = await apiClient.get(`${this.basePath}/template`, {
      responseType: 'blob',
    });
    return response as unknown as Blob;
  }

  /**
   * Touch a submission to refresh data and update last_edited_at.
   * Currently implemented as a GET to sync data.
   * Can be updated to use a dedicated endpoint when available.
   */
  async touchSubmission(submissionId: string): Promise<ManualSubmission> {
    return this.getSubmission(submissionId);
  }
}

export const manualEntryApi = new ManualEntryApiClient();
